import server from 'common/utils/server';
import { getSearchFilter } from 'common/utils/locationUtils';

export function scorecardStatsState(
  state = {
    scorecardStats: [],
    scorecardStatsByScorecardId: {},
    scorecardTimeAggregateDates: [],
    scorecardTimeAggregateScores: [],
    scorecardStatDetailsByScorecardId: {},
    scorecardStatsSortBy: null,
    scorecardStatsSortDirection: null,
  },
  action
) {
  switch (action.type) {
    case 'qa_scorecard_stats/RECEIVED_SCORECARD_OVERVIEW': {
      const { scorecardStats, scorecardTimeAggregateDates, scorecardTimeAggregateScores } =
        action.value;
      const statsById = { ...state.scorecardStatsByScorecardId };
      scorecardStats.forEach(stat => {
        statsById[stat.scorecardId] = stat;
      });
      return {
        ...state,
        scorecardStats: action.value.scorecardStats,
        scorecardStatsByScorecardId: statsById,
        scorecardTimeAggregateDates,
        scorecardTimeAggregateScores,
      };
    }
    case 'qa_scorecard_details/RECEIVED_SCORECARD_DETAIL': {
      const { scorecardDetail } = action;
      const { scorecardId } = scorecardDetail;
      const scorecardStatDetailsByScorecardId = {
        ...state.scorecardStatDetailsByScorecardId,
      };
      scorecardStatDetailsByScorecardId[scorecardId] = scorecardDetail;
      return {
        ...state,
        scorecardStatDetailsByScorecardId,
      };
    }
    case 'qa_scorecard_details/SORT_SCORECARD_STATS': {
      const { sortBy } = action;
      const { scorecardStatsSortBy } = state;
      let { scorecardStatsSortDirection } = state;
      if (sortBy === scorecardStatsSortBy) {
        scorecardStatsSortDirection =
          scorecardStatsSortDirection === 'ascending' ? 'descending' : 'ascending';
      } else {
        scorecardStatsSortDirection = 'ascending';
      }
      return {
        ...state,
        scorecardStatsSortBy: sortBy,
        scorecardStatsSortDirection,
      };
    }
    default: {
      return state;
    }
  }
}

export const loadScorecardStats =
  (
    history,
    { customProperties, snapshotFilters = {}, interactionSnapshotFiltersEnabled = false } = {}
  ) =>
  async (dispatch, getState) => {
    const {
      startDate,
      endDate,
      teamIds,
      groupIds,
      reportingGroupIds,
      employeeIds,
      reviewerIds,
      autofailed,
      languages,
      channelEnumIds,
      propertyIds,
    } = getSearchFilter(history);
    const { scorecardStatsState } = getState();
    const { scorecardStatsSortBy, scorecardStatsSortDirection } = scorecardStatsState;
    const queryParams = {
      completed_from: startDate,
      completed_to: endDate,
      by_reviewer_id: reviewerIds,
      by_employee_id: employeeIds,
      ...(!interactionSnapshotFiltersEnabled && { by_language: languages }),
      by_channel_enum_id: channelEnumIds,
      by_property_id: propertyIds,
      for_teams: teamIds,
      for_groups: groupIds,
      for_reporting_groups: reportingGroupIds,
      autofailed,
      ...(customProperties ? { custom_properties: customProperties } : {}),
      ...snapshotFilters,
    };
    if (scorecardStatsSortBy) {
      queryParams.order_by = scorecardStatsSortBy;
      queryParams.order_direction = scorecardStatsSortDirection === 'descending' ? 'desc' : 'asc';
    }
    const [trendsOverTimeResponse, overviewTableResponse] = await Promise.all([
      // we may want to remove this endpoint
      // server.get('/api/v1/qa_reporting/scorecards_overview_chart', queryParams),
      server.get('/api/v1/qa_reporting/trends_over_time', queryParams),
      server.get('/api/v1/qa_reporting/scorecards_overview', queryParams),
    ]);
    const trendsTotals = trendsOverTimeResponse.scorecard_overview_totals;
    dispatch({
      type: 'qa_scorecard_stats/RECEIVED_SCORECARD_OVERVIEW',
      value: {
        scorecardStats: overviewTableResponse.scorecards_overview.map(sc => ({
          scorecardName: sc.scorecard,
          scorecardId: sc.scorecard_id,
          averageScore: sc.average_score,
          scoredReviewCount: sc.reviews_scored,
          scoredWithAnnotationsOnlyCount: sc.annotations_only,
          scoredTeamMemberCount: sc.team_members,
        })),
        scorecardTimeAggregateDates: trendsOverTimeResponse.dates,
        scorecardTimeAggregateScores: trendsTotals.map(ot => ({
          scorecardName: ot.scorecard_name,
          scorecardId: ot.scorecard_id,
          averageScores: ot.average_scores,
          scoreCounts: ot.reviews_scored_counts,
        })),
      },
    });
  };

export const loadScorecardStatDetails =
  ({
    history,
    scorecardId,
    customProperties,
    snapshotFilters = {},
    interactionSnapshotFiltersEnabled = false,
  }) =>
  async dispatch => {
    const {
      startDate,
      endDate,
      teamIds,
      groupIds,
      reportingGroupIds,
      employeeIds,
      reviewerIds,
      languages,
      channelEnumIds,
      propertyIds,
    } = getSearchFilter(history);
    const queryParams = {
      qa_form_id: scorecardId,
      completed_from: startDate,
      completed_to: endDate,
      by_reviewer_id: reviewerIds,
      by_employee_id: employeeIds,
      ...(!interactionSnapshotFiltersEnabled && { by_language: languages }),
      by_channel_enum_id: channelEnumIds,
      by_property_id: propertyIds,
      for_teams: teamIds,
      for_groups: groupIds,
      for_reporting_groups: reportingGroupIds,
      ...(customProperties ? { custom_properties: customProperties } : {}),
      ...snapshotFilters,
    };
    const [sectionBreakdownResponse, trendsOverTimeResponse, questionBreakdownResponse] =
      await Promise.all([
        server.get('/api/v1/qa_reporting/scorecard_section_breakdown', queryParams),
        server.get('/api/v1/qa_reporting/scorecard_section_trends_over_time', queryParams),
        server.get('/api/v1/qa_reporting/scorecard_question_breakdown', queryParams),
      ]);
    const sectionDetails = sectionBreakdownResponse.sections.map(detail => ({
      sectionTitle: detail.section_title,
      averageScore: detail.average_score,
    }));
    const trendsOverTimeDates = trendsOverTimeResponse.dates;
    const trendsOverTimeSections = trendsOverTimeResponse.sections.map(section => ({
      sectionId: section.section_id,
      sectionTitle: section.section_title,
      averageScores: section.averages,
    }));
    const sectionQuestionDetails = questionBreakdownResponse.scorecards.map(section => ({
      sectionTitle: section.section_title,
      questions: section.questions.map(question => ({
        answersCriteria: question.answers_criteria || {},
        questionId: question.question_id,
        questionTitle: question.question_title,
        totalAnswers: question.total_answers,
        answerCountByAnswerText: question.options,
      })),
    }));
    const scorecardDetail = {
      scorecardId,
      sectionDetails,
      trendsOverTimeDates,
      trendsOverTimeSections,
      sectionQuestionDetails,
    };
    dispatch({
      type: 'qa_scorecard_details/RECEIVED_SCORECARD_DETAIL',
      scorecardDetail,
    });
  };

export const sortScorecardStats =
  ({ history, sortBy }) =>
  dispatch => {
    dispatch({
      type: 'qa_scorecard_details/SORT_SCORECARD_STATS',
      sortBy,
    });
    dispatch(loadScorecardStats(history));
  };
